import { useState, useEffect, useCallback } from 'react'
import { Navigate } from 'react-router-dom'
import { useAuth } from '../../context/AuthContext'
import { Layout } from '../../components/Layout'
import { hasAnyRole } from '../../utils/permissions'
import { supabase } from '../../services/supabase'
import { logActivity } from '../../utils/activityLogger'
import { Archive, Eye, EyeOff, X, BarChart3, Search } from 'lucide-react'

const MudurNotifications = () => {
  const { userProfile, loading: authLoading } = useAuth()
  const [notifications, setNotifications] = useState([])
  const [loading, setLoading] = useState(true)
  const [selectedNotification, setSelectedNotification] = useState(null)
  const [statusFilter, setStatusFilter] = useState('all')
  const [categoryFilter, setCategoryFilter] = useState('all')
  const [showArchived, setShowArchived] = useState(false)
  const [notificationCategories, setNotificationCategories] = useState([])
  const [notificationStats, setNotificationStats] = useState([])
  const [statsLoading, setStatsLoading] = useState(false)
  const [statsStatusFilter, setStatsStatusFilter] = useState('all')
  const [statsSearchTerm, setStatsSearchTerm] = useState('')
  const [activeTab, setActiveTab] = useState('details') // 'details' veya 'stats'

  const statusOptions = [
    { value: 'Okundu', label: 'Okundu', color: 'blue' },
    { value: 'Okunmadı', label: 'Okunmadı', color: 'gray' },
    { value: 'İlgili Değil', label: 'İlgili Değil', color: 'yellow' },
    { value: 'Yaptım', label: 'Yaptım', color: 'green' },
    { value: 'Yapmadım', label: 'Yapmadım', color: 'red' },
    { value: 'Sorun Oluştu', label: 'Sorun Oluştu', color: 'orange' },
  ]

  const fetchCategories = async () => {
    try {
      const { data, error } = await supabase
        .from('notification_categories')
        .select('*')
        .eq('is_active', true)
        .order('display_name')

      if (error) throw error
      setNotificationCategories(data || [])
    } catch (error) {
      console.error('Kategoriler yüklenemedi:', error)
    }
  }

  const fetchNotifications = useCallback(async () => {
    try {
      setLoading(true)

      // Önce arşivlenmiş bildirimleri kontrol et
      const { data: archivedData } = await supabase
        .from('notification_archive')
        .select('notification_id')
        .eq('school_id', userProfile.school_id)

      const archivedIds = archivedData?.map((a) => a.notification_id) || []

      // Bildirimleri getir (kategoriler olmadan)
      let query = supabase
        .from('notifications')
        .select(`
          *,
          created_by_user:users!notifications_created_by_fkey(full_name, email),
          targets:notification_targets!inner(school_id),
          status:notification_status(school_id, status, notes, updated_at),
          attachments:notification_attachments(*)
        `)
        .eq('notification_targets.school_id', userProfile.school_id)
        .order('created_at', { ascending: false })

      const { data, error } = await query

      if (error) throw error

      // Bildirim ID'lerini topla
      const notificationIds = (data || []).map(n => n.id)

      // Kategorileri ayrı bir sorgu ile çek
      let categoriesData = []
      if (notificationIds.length > 0) {
        const { data: categoryRelations, error: categoryError } = await supabase
          .from('notification_category_relations')
          .select(`
            notification_id,
            category_id,
            category:notification_categories(*)
          `)
          .in('notification_id', notificationIds)

        if (categoryError) {
          console.error('Kategoriler yüklenemedi:', categoryError)
        } else {
          categoriesData = categoryRelations || []
        }
      }

      // Bildirimlere kategorileri ekle
      const notificationsWithCategories = (data || []).map(notification => ({
        ...notification,
        categories: categoriesData
          .filter(rel => rel.notification_id === notification.id)
          .map(rel => ({
            category_id: rel.category_id,
            category: rel.category
          }))
      }))

      // Arşiv filtresi uygula
      let filtered = notificationsWithCategories
      if (!showArchived) {
        filtered = filtered.filter((n) => !archivedIds.includes(n.id))
      } else {
        filtered = filtered.filter((n) => archivedIds.includes(n.id))
      }

      // Durum filtresi uygula
      if (statusFilter !== 'all') {
        filtered = filtered.filter((n) => {
          const status = n.status?.find((s) => s.school_id === userProfile.school_id)
          return status?.status === statusFilter
        })
      }

      // Kategori filtresi uygula
      if (categoryFilter !== 'all') {
        filtered = filtered.filter((n) => {
          return n.categories?.some((rel) => rel.category?.id === categoryFilter)
        })
      }

      setNotifications(filtered)
    } catch (error) {
      console.error('Bildirimler yüklenemedi:', error)
      alert('Bildirimler yüklenirken hata oluştu: ' + error.message)
    } finally {
      setLoading(false)
    }
  }, [userProfile, statusFilter, categoryFilter, showArchived])

  useEffect(() => {
    if (userProfile && userProfile.school_id) {
      fetchNotifications()
      fetchCategories()
    }
  }, [userProfile, fetchNotifications])

  // Modal açıldığında istatistikleri getir
  useEffect(() => {
    if (selectedNotification && activeTab === 'stats') {
      fetchNotificationStats(selectedNotification.id)
    }
  }, [selectedNotification, activeTab])

  if (authLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-lg">Yükleniyor...</div>
      </div>
    )
  }

  if (!userProfile || !hasAnyRole(userProfile, ['mudur'])) {
    return <Navigate to="/unauthorized" replace />
  }

  if (!userProfile.school_id) {
    return (
      <Layout>
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <p className="text-yellow-800">
            Okul bilginiz bulunmamaktadır. Lütfen yönetici ile iletişime geçin.
          </p>
        </div>
      </Layout>
    )
  }

  const updateStatus = async (notificationId, status, notes = '') => {
    try {
      // Bildirim bilgisini al
      const { data: notification } = await supabase
        .from('notifications')
        .select('title')
        .eq('id', notificationId)
        .single()

      const existingStatus = await supabase
        .from('notification_status')
        .select('*')
        .eq('notification_id', notificationId)
        .eq('school_id', userProfile.school_id)
        .single()

      if (existingStatus.data) {
        // Güncelle
        const { error } = await supabase
          .from('notification_status')
          .update({
            status,
            notes,
            updated_by: userProfile.id,
            updated_at: new Date().toISOString(),
          })
          .eq('notification_id', notificationId)
          .eq('school_id', userProfile.school_id)

        if (error) throw error
      } else {
        // Yeni oluştur
        const { error } = await supabase.from('notification_status').insert({
          notification_id: notificationId,
          school_id: userProfile.school_id,
          status,
          notes,
          updated_by: userProfile.id,
        })

        if (error) throw error
      }

      // Activity log
      await logActivity(
        userProfile.id,
        'notification_status_updated',
        'notification',
        notificationId,
        `Bildirim durumu güncellendi: ${notification?.title || notificationId} - ${status}${notes ? ` (Not: ${notes})` : ''}`
      )

      await fetchNotifications()
      setSelectedNotification(null)
    } catch (error) {
      alert('Durum güncellenirken hata oluştu: ' + error.message)
    }
  }

  const archiveNotification = async (notificationId) => {
    try {
      // Bildirim bilgisini al
      const { data: notification } = await supabase
        .from('notifications')
        .select('title')
        .eq('id', notificationId)
        .single()

      const { error } = await supabase.from('notification_archive').insert({
        notification_id: notificationId,
        school_id: userProfile.school_id,
        archived_by: userProfile.id,
      })

      if (error) throw error

      // Activity log
      await logActivity(
        userProfile.id,
        'notification_archived',
        'notification',
        notificationId,
        `Bildirim arşivlendi: ${notification?.title || notificationId}`
      )

      await fetchNotifications()
      setSelectedNotification(null)
    } catch (error) {
      alert('Arşivleme başarısız: ' + error.message)
    }
  }

  const unarchiveNotification = async (notificationId) => {
    try {
      // Bildirim bilgisini al
      const { data: notification } = await supabase
        .from('notifications')
        .select('title')
        .eq('id', notificationId)
        .single()

      const { error } = await supabase
        .from('notification_archive')
        .delete()
        .eq('notification_id', notificationId)
        .eq('school_id', userProfile.school_id)

      if (error) throw error

      // Activity log
      await logActivity(
        userProfile.id,
        'notification_unarchived',
        'notification',
        notificationId,
        `Bildirim arşivden çıkarıldı: ${notification?.title || notificationId}`
      )

      await fetchNotifications()
    } catch (error) {
      alert('Arşivden çıkarma başarısız: ' + error.message)
    }
  }

  const getStatusForNotification = (notification) => {
    const status = notification.status?.find((s) => s.school_id === userProfile.school_id)
    return status?.status || 'Okunmadı'
  }

  // Bildirim istatistiklerini getir
  const fetchNotificationStats = async (notificationId) => {
    try {
      setStatsLoading(true)
      
      // Bildirimin hedef okullarını getir
      const { data: targets, error: targetsError } = await supabase
        .from('notification_targets')
        .select(`
          school_id,
          school:schools(id, okul_adi)
        `)
        .eq('notification_id', notificationId)

      if (targetsError) throw targetsError

      // Her okulun durumunu getir
      const { data: statuses, error: statusError } = await supabase
        .from('notification_status')
        .select('*')
        .eq('notification_id', notificationId)

      if (statusError) throw statusError

      // Okulları ve durumlarını birleştir
      const stats = (targets || []).map(target => {
        const status = statuses?.find(s => s.school_id === target.school_id)
        return {
          school_id: target.school_id,
          school_name: target.school?.okul_adi || 'Bilinmeyen Okul',
          status: status?.status || 'Okunmadı',
          notes: status?.notes || '',
          updated_at: status?.updated_at || null,
          updated_by: status?.updated_by || null
        }
      })

      setNotificationStats(stats)
    } catch (error) {
      console.error('İstatistikler yüklenemedi:', error)
      alert('İstatistikler yüklenirken hata oluştu: ' + error.message)
    } finally {
      setStatsLoading(false)
    }
  }

  if (loading) {
    return (
      <Layout>
        <div className="text-center py-12">Yükleniyor...</div>
      </Layout>
    )
  }

  return (
    <Layout>
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900">Bildirimlerim</h1>
        <p className="text-gray-600 mt-1">Size gelen bildirimleri görüntüleyin ve yönetin</p>
      </div>

      <div className="flex justify-between items-center mb-6">
        <div className="flex items-center space-x-4">
          <button
            onClick={() => setShowArchived(!showArchived)}
            className={`flex items-center space-x-2 px-4 py-2 rounded-lg transition-all duration-200 ${
              showArchived
                ? 'bg-gradient-to-r from-blue-600 to-blue-700 text-white shadow-md'
                : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
            }`}
          >
            {showArchived ? <Eye size={18} /> : <EyeOff size={18} />}
            <span>{showArchived ? 'Arşivlenmişler' : 'Aktifler'}</span>
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Durum Filtresi
          </label>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
          >
            <option value="all">Tümü</option>
            {statusOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Kategori Filtresi
          </label>
          <select
            value={categoryFilter}
            onChange={(e) => setCategoryFilter(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
          >
            <option value="all">Tüm Kategoriler</option>
            {notificationCategories.map((category) => (
              <option key={category.id} value={category.id}>
                {category.display_name}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="space-y-4">
        {notifications.length === 0 ? (
          <div className="bg-white p-8 rounded-lg shadow text-center text-gray-500">
            Bildirim bulunmamaktadır.
          </div>
        ) : (
          notifications.map((notification) => {
            const currentStatus = getStatusForNotification(notification)
            const statusOption = statusOptions.find((o) => o.value === currentStatus)
            
            // Son tarih kontrolü
            const isOverdue = notification.son_tarih && 
              new Date(notification.son_tarih) < new Date() && 
              currentStatus !== 'Yaptım'
            
            // Kart rengi - son tarih geçtiyse ve yaptım değilse kırmızı
            const cardClassName = isOverdue
              ? 'bg-red-50 border-red-300 rounded-xl shadow-sm p-6 hover:shadow-md hover:border-red-400 transition-all duration-200 cursor-pointer relative overflow-hidden'
              : 'bg-white rounded-xl shadow-sm border border-gray-200 p-6 hover:shadow-md hover:border-blue-300 transition-all duration-200 cursor-pointer relative overflow-hidden'

            return (
              <div
                key={notification.id}
                className={cardClassName}
                onClick={() => setSelectedNotification(notification)}
              >
                {/* Acil/Önemli Bayrak - Sol Üst Köşe */}
                {(notification.is_urgent || notification.is_important) && (
                  <div className="absolute top-0 left-0 z-10" style={{ width: '0', height: '0' }}>
                    {/* Ana bayrak üçgeni */}
                    <div 
                      className="absolute"
                      style={{
                        width: '0',
                        height: '0',
                        borderStyle: 'solid',
                        borderWidth: '60px 60px 0 0',
                        borderColor: notification.is_urgent 
                          ? '#DC2626 transparent transparent transparent'
                          : '#EA580C transparent transparent transparent',
                        filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.3))'
                      }}
                    />
                    {/* İkinci renk (hem acil hem önemli ise) */}
                    {notification.is_urgent && notification.is_important && (
                      <div 
                        className="absolute"
                        style={{
                          top: '0',
                          left: '0',
                          width: '0',
                          height: '0',
                          borderStyle: 'solid',
                          borderWidth: '30px 30px 0 0',
                          borderColor: '#EA580C transparent transparent transparent'
                        }}
                      />
                    )}
                    {/* Bayrak metni */}
                    <span 
                      className="absolute text-white text-[10px] font-bold whitespace-nowrap pointer-events-none"
                      style={{
                        top: '15px',
                        left: '10px',
                        transform: 'rotate(-45deg)',
                        transformOrigin: '0 0',
                        textShadow: '0 1px 3px rgba(0,0,0,0.6)',
                        letterSpacing: '0.3px',
                        lineHeight: '1.2'
                      }}
                    >
                      {notification.is_urgent && notification.is_important 
                        ? 'ACİL & ÖNEMLİ' 
                        : notification.is_urgent 
                        ? 'ACİL' 
                        : 'ÖNEMLİ'}
                    </span>
                  </div>
                )}
                
                {/* Kategoriler - Sağ Üst */}
                {notification.categories && notification.categories.length > 0 && (
                  <div className="absolute top-4 right-4 flex items-center space-x-1 flex-wrap gap-1 justify-end max-w-[200px]">
                    {notification.categories.map((rel, idx) => (
                      rel.category && (
                        <span
                          key={idx}
                          className="px-2 py-1 text-xs rounded text-gray-700 border"
                          style={{ 
                            backgroundColor: `${rel.category.color || '#3B82F6'}20`,
                            borderColor: `${rel.category.color || '#3B82F6'}40`,
                            color: rel.category.color || '#3B82F6'
                          }}
                        >
                          {rel.category.display_name}
                        </span>
                      )
                    ))}
                  </div>
                )}
                
                <div className="flex justify-between items-start pr-32">
                  <div className="flex-1">
                    {/* Başlık ve Durum */}
                    <div className="flex items-center space-x-3 mb-2 flex-wrap gap-2">
                      <h3 className={`text-lg font-semibold ${isOverdue ? 'text-red-900' : 'text-gray-900'} ${(notification.is_urgent || notification.is_important) ? 'pl-14' : ''}`}>
                        {notification.title}
                      </h3>
                      <span
                        className={`px-2 py-1 text-xs rounded font-medium ${
                          statusOption
                            ? statusOption.color === 'blue' ? 'bg-blue-100 text-blue-800'
                            : statusOption.color === 'yellow' ? 'bg-yellow-100 text-yellow-800'
                            : statusOption.color === 'green' ? 'bg-green-100 text-green-800'
                            : statusOption.color === 'red' ? 'bg-red-100 text-red-800'
                            : statusOption.color === 'orange' ? 'bg-orange-100 text-orange-800'
                            : 'bg-gray-100 text-gray-800'
                            : 'bg-gray-100 text-gray-800'
                        }`}
                      >
                        {currentStatus}
                      </span>
                    </div>
                    {notification.description && (
                      <p className={`mb-2 line-clamp-2 ${isOverdue ? 'text-red-700' : 'text-gray-600'}`}>
                        {notification.description}
                      </p>
                    )}
                    <div className={`flex items-center space-x-4 text-sm ${isOverdue ? 'text-red-600' : 'text-gray-500'}`}>
                      <span>
                        {new Date(notification.created_at).toLocaleDateString('tr-TR')}
                      </span>
                      {notification.son_tarih && (
                        <span className={isOverdue ? 'font-semibold' : ''}>
                          Son Tarih:{' '}
                          {new Date(notification.son_tarih).toLocaleDateString('tr-TR')}
                          {isOverdue && ' ⚠️ (Geçti!)'}
                        </span>
                      )}
                      {notification.attachments && notification.attachments.length > 0 && (
                        <span>{notification.attachments.length} ek</span>
                      )}
                    </div>
                    
                    {/* Hızlı Durum Güncelleme Butonları */}
                    <div className="mt-4 pt-4 border-t border-gray-200" onClick={(e) => e.stopPropagation()}>
                      <div className="flex flex-wrap gap-2">
                        {statusOptions
                          .filter(option => ['Okundu', 'İlgili Değil', 'Yaptım', 'Sorun Oluştu', 'Yapmadım'].includes(option.label))
                          .map((option) => {
                            const isActive = currentStatus === option.value
                            let buttonClass = 'px-3 py-1.5 text-xs font-medium rounded-lg transition-all duration-200 '
                            
                            if (isActive) {
                              // Aktif durum için - tam renkli
                              if (option.color === 'blue') buttonClass += 'bg-blue-600 text-white hover:bg-blue-700'
                              else if (option.color === 'yellow') buttonClass += 'bg-yellow-600 text-white hover:bg-yellow-700'
                              else if (option.color === 'green') buttonClass += 'bg-green-600 text-white hover:bg-green-700'
                              else if (option.color === 'orange') buttonClass += 'bg-orange-600 text-white hover:bg-orange-700'
                              else if (option.color === 'red') buttonClass += 'bg-red-600 text-white hover:bg-red-700'
                            } else {
                              // Pasif durum için - açık gri tonlar
                              buttonClass += 'bg-gray-100 text-gray-500 hover:bg-gray-200 hover:text-gray-600'
                            }
                            
                            return (
                              <button
                                key={option.value}
                                onClick={() => updateStatus(notification.id, option.value)}
                                className={buttonClass}
                              >
                                {option.label}
                              </button>
                            )
                          })}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )
          })
        )}
      </div>

      {/* Detay Modal */}
      {selectedNotification && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-5xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-start mb-4">
                <h2 className="text-2xl font-bold text-gray-900">
                  {selectedNotification.title}
                </h2>
                <button
                  onClick={() => {
                    setSelectedNotification(null)
                    setActiveTab('details')
                    setStatsStatusFilter('all')
                    setStatsSearchTerm('')
                  }}
                  className="text-gray-500 hover:text-gray-700"
                >
                  <X size={24} />
                </button>
              </div>

              {/* Tab Menüsü */}
              <div className="flex border-b border-gray-200 mb-6">
                <button
                  onClick={() => setActiveTab('details')}
                  className={`px-4 py-2 font-medium text-sm transition-colors ${
                    activeTab === 'details'
                      ? 'border-b-2 border-blue-600 text-blue-600'
                      : 'text-gray-500 hover:text-gray-700'
                  }`}
                >
                  Detaylar
                </button>
                <button
                  onClick={() => {
                    setActiveTab('stats')
                    if (selectedNotification) {
                      fetchNotificationStats(selectedNotification.id)
                    }
                  }}
                  className={`px-4 py-2 font-medium text-sm transition-colors flex items-center gap-2 ${
                    activeTab === 'stats'
                      ? 'border-b-2 border-blue-600 text-blue-600'
                      : 'text-gray-500 hover:text-gray-700'
                  }`}
                >
                  <BarChart3 size={18} />
                  İstatistikler
                </button>
              </div>

              {/* Detaylar Sekmesi */}
              {activeTab === 'details' && (
                <>
                  {selectedNotification.description && (
                    <div className="mb-4">
                      <p className="text-gray-700 whitespace-pre-wrap">
                        {selectedNotification.description}
                      </p>
                    </div>
                  )}

                  <div className="mb-4 text-sm text-gray-500">
                    <p>
                      Oluşturulma: {new Date(selectedNotification.created_at).toLocaleString('tr-TR')}
                    </p>
                    {selectedNotification.son_tarih && (
                      <p>
                        Son Tarih: {new Date(selectedNotification.son_tarih).toLocaleDateString('tr-TR')}
                      </p>
                    )}
                  </div>

                  {selectedNotification.attachments && selectedNotification.attachments.length > 0 && (
                    <div className="mb-4">
                      <h3 className="font-semibold mb-2">Ekler</h3>
                      <div className="grid grid-cols-2 gap-4">
                        {selectedNotification.attachments.map((attachment) => (
                          <div key={attachment.id}>
                            {attachment.file_type === 'image' ? (
                              <img
                                src={attachment.file_path}
                                alt={attachment.file_name}
                                className="w-full h-48 object-cover rounded border"
                              />
                            ) : (
                              <video
                                src={attachment.file_path}
                                controls
                                className="w-full h-48 rounded border"
                              />
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  <div className="mb-4">
                    <h3 className="font-semibold mb-2">Durum Güncelle</h3>
                    <div className="grid grid-cols-2 gap-2">
                      {statusOptions.map((option) => (
                        <button
                          key={option.value}
                          onClick={() => updateStatus(selectedNotification.id, option.value)}
                          className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                            getStatusForNotification(selectedNotification) === option.value
                              ? `bg-gradient-to-r from-${option.color}-600 to-${option.color}-700 text-white shadow-md`
                              : `bg-${option.color}-100 text-${option.color}-800 hover:bg-${option.color}-200 border border-${option.color}-200`
                          }`}
                        >
                          {option.label}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="flex justify-end space-x-3">
                    {showArchived ? (
                      <button
                        onClick={() => unarchiveNotification(selectedNotification.id)}
                        className="px-4 py-2 bg-gradient-to-r from-green-600 to-green-700 text-white rounded-lg hover:from-green-700 hover:to-green-800 shadow-md transition-all duration-200"
                      >
                        Arşivden Çıkar
                      </button>
                    ) : (
                      <button
                        onClick={() => archiveNotification(selectedNotification.id)}
                        className="flex items-center space-x-2 px-4 py-2 bg-gradient-to-r from-gray-600 to-gray-700 text-white rounded-lg hover:from-gray-700 hover:to-gray-800 shadow-md transition-all duration-200"
                      >
                        <Archive size={18} />
                        <span>Arşive At</span>
                      </button>
                    )}
                  </div>
                </>
              )}

              {/* İstatistikler Sekmesi */}
              {activeTab === 'stats' && (
                <div>
                  {statsLoading ? (
                    <div className="text-center py-12">
                      <div className="text-lg">Yükleniyor...</div>
                    </div>
                  ) : (
                    <>
                      {/* İstatistik Özeti */}
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                          <div className="text-2xl font-bold text-blue-600">
                            {notificationStats.length}
                          </div>
                          <div className="text-sm text-blue-700">Toplam Okul</div>
                        </div>
                        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                          <div className="text-2xl font-bold text-green-600">
                            {notificationStats.filter(s => s.status === 'Yaptım').length}
                          </div>
                          <div className="text-sm text-green-700">Yaptı</div>
                        </div>
                        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                          <div className="text-2xl font-bold text-red-600">
                            {notificationStats.filter(s => s.status === 'Yapmadım').length}
                          </div>
                          <div className="text-sm text-red-700">Yapmadı</div>
                        </div>
                        <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                          <div className="text-2xl font-bold text-gray-600">
                            {notificationStats.filter(s => s.status === 'Okunmadı').length}
                          </div>
                          <div className="text-sm text-gray-700">Okunmadı</div>
                        </div>
                      </div>

                      {/* Filtreler */}
                      <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 mb-4">
                        <div className="flex flex-col md:flex-row gap-4">
                          <div className="flex-1">
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Durum Filtresi
                            </label>
                            <select
                              value={statsStatusFilter}
                              onChange={(e) => setStatsStatusFilter(e.target.value)}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                            >
                              <option value="all">Tüm Durumlar</option>
                              {statusOptions.map(option => (
                                <option key={option.value} value={option.value}>
                                  {option.label}
                                </option>
                              ))}
                            </select>
                          </div>
                          <div className="flex-1">
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Okul Ara
                            </label>
                            <div className="relative">
                              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
                              <input
                                type="text"
                                value={statsSearchTerm}
                                onChange={(e) => setStatsSearchTerm(e.target.value)}
                                placeholder="Okul adı ile ara..."
                                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                              />
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Okul Listesi */}
                      <div className="border border-gray-200 rounded-lg overflow-hidden">
                        <div className="bg-gray-50 border-b border-gray-200 px-4 py-3">
                          <h3 className="font-semibold text-gray-900">Okul Durumları</h3>
                        </div>
                        <div className="max-h-[400px] overflow-y-auto">
                          {(() => {
                            // Filtreleme
                            let filteredStats = notificationStats
                            
                            if (statsStatusFilter !== 'all') {
                              filteredStats = filteredStats.filter(s => s.status === statsStatusFilter)
                            }
                            
                            if (statsSearchTerm) {
                              filteredStats = filteredStats.filter(s =>
                                s.school_name.toLowerCase().includes(statsSearchTerm.toLowerCase())
                              )
                            }

                            if (filteredStats.length === 0) {
                              return (
                                <div className="p-8 text-center text-gray-500">
                                  Sonuç bulunamadı
                                </div>
                              )
                            }

                            return (
                              <table className="w-full">
                                <thead className="bg-gray-50 sticky top-0">
                                  <tr>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                      Okul Adı
                                    </th>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                      Durum
                                    </th>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                      Güncelleme Tarihi
                                    </th>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                      Notlar
                                    </th>
                                  </tr>
                                </thead>
                                <tbody className="bg-white divide-y divide-gray-200">
                                  {filteredStats.map((stat, idx) => {
                                    const statusOption = statusOptions.find(o => o.value === stat.status)
                                    return (
                                      <tr key={idx} className="hover:bg-gray-50">
                                        <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900">
                                          {stat.school_name}
                                        </td>
                                        <td className="px-4 py-3 whitespace-nowrap">
                                          <span
                                            className={`px-2 py-1 text-xs rounded font-medium ${
                                              statusOption
                                                ? statusOption.color === 'blue' ? 'bg-blue-100 text-blue-800'
                                                : statusOption.color === 'yellow' ? 'bg-yellow-100 text-yellow-800'
                                                : statusOption.color === 'green' ? 'bg-green-100 text-green-800'
                                                : statusOption.color === 'red' ? 'bg-red-100 text-red-800'
                                                : statusOption.color === 'orange' ? 'bg-orange-100 text-orange-800'
                                                : 'bg-gray-100 text-gray-800'
                                                : 'bg-gray-100 text-gray-800'
                                            }`}
                                          >
                                            {stat.status}
                                          </span>
                                        </td>
                                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                                          {stat.updated_at
                                            ? new Date(stat.updated_at).toLocaleString('tr-TR')
                                            : '-'}
                                        </td>
                                        <td className="px-4 py-3 text-sm text-gray-500">
                                          {stat.notes || '-'}
                                        </td>
                                      </tr>
                                    )
                                  })}
                                </tbody>
                              </table>
                            )
                          })()}
                        </div>
                      </div>
                    </>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </Layout>
  )
}

export default MudurNotifications

